import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "../../lib/api";
import { PageHeader, Loading, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Building2, Wrench, GraduationCap, CheckCircle2, XCircle, Phone, Mail, MessageSquare, CalendarClock, FileWarning, ClipboardList, Download } from "lucide-react";

const TYPES = [{ k: "society", label: "Society", Icon: Building2 }, { k: "vendor", label: "Vendor", Icon: Wrench }, { k: "expert", label: "Expert", Icon: GraduationCap }];
const QUICK_STATUS = [["", "All"], ["APPLICATION_SUBMITTED", "New"], ["UNDER_REVIEW", "Under Review"], ["DOCUMENTS_REQUESTED", "Docs Requested"], ["APPROVED,ACTIVATED", "Approved"], ["REJECTED", "Rejected"]];
const STATUS_TONE = { APPLICATION_SUBMITTED: "bg-amber-100 text-amber-800", UNDER_REVIEW: "bg-blue-100 text-blue-800", CONTACTED: "bg-blue-100 text-blue-800", MEETING_SCHEDULED: "bg-indigo-100 text-indigo-800", DOCUMENTS_REQUESTED: "bg-orange-100 text-orange-800", APPROVED: "bg-emerald-100 text-emerald-800", ACTIVATED: "bg-emerald-100 text-emerald-800", REJECTED: "bg-red-100 text-red-700", ON_HOLD: "bg-muted text-muted-foreground" };

export default function AdminApplications() {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({
    type: searchParams.get("type") || "",
    status: searchParams.get("pending") === "true"
      ? "APPLICATION_SUBMITTED,UNDER_REVIEW,CONTACTED,MEETING_SCHEDULED,DOCUMENTS_REQUESTED,ON_HOLD"
      : searchParams.get("status") || "",
    q: searchParams.get("q") || "",
  });
  const [apps, setApps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const loadSummary = () => api.get("/admin/applications/summary").then((r) => setSummary(r.data));
  const loadApps = () => {
    const p = new URLSearchParams();
    if (filter.type) p.set("type", filter.type);
    if (filter.status) p.set("status", filter.status);
    if (filter.q) p.set("q", filter.q);
    api.get(`/admin/applications?${p}`).then((r) => setApps(r.data));
  };
  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadApps(); }, [filter]);

  const openDetail = (id) => api.get(`/admin/applications/${id}`).then((r) => setDetail(r.data));
  const act = async (id, path, body, msg) => {
    try { await api.post(`/admin/applications/${id}/${path}`, body || {}); toast.success(msg || "Done"); openDetail(id); loadApps(); loadSummary(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!summary) return <Loading />;

  return (
    <div>
      <PageHeader title="Applications" subtitle="Review and approve Society, Vendor and Expert applications." />
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {TYPES.map(({ k, label, Icon }) => {
          const c = summary[k] || {};
          return (
            <div key={k} className="bg-card border rounded-xl p-5" data-testid={`app-card-${k}`}>
              <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div><span className="font-display font-bold">{label} Applications</span></div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                {[["new", "New"], ["under_review", "Review"], ["approved", "Approved"], ["rejected", "Rejected"]].map(([sk, sl]) => (
                  <button key={sk} onClick={() => setFilter({ type: k, status: sk === "new" ? "APPLICATION_SUBMITTED" : sk === "approved" ? "APPROVED,ACTIVATED" : sk === "rejected" ? "REJECTED" : "UNDER_REVIEW", q: "" })}
                    className="rounded-lg p-2 hover:bg-muted transition-colors" data-testid={`app-count-${k}-${sk}`}>
                    <div className="font-display font-black text-lg">{c[sk] ?? 0}</div><div className="text-[10px] text-muted-foreground">{sl}</div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Input placeholder="Search name / email / mobile / ID" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="max-w-xs" data-testid="app-search" />
        <select className="border rounded-lg px-3 text-sm" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} data-testid="app-filter-type">
          <option value="">All types</option>{TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
        </select>
        {(filter.type || filter.status || filter.q) && <Button variant="ghost" size="sm" onClick={() => setFilter({ type: "", status: "", q: "" })}>Clear</Button>}
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="app-quick-filters">
        {QUICK_STATUS.map(([sv, sl]) => (
          <button key={sl} onClick={() => setFilter({ ...filter, status: sv })} data-testid={`app-chip-${sl.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter.status === sv ? "bg-primary text-white border-primary" : "bg-background hover:bg-muted"}`}>
            {sl}
          </button>
        ))}
      </div>

      {apps.length === 0 ? <EmptyState icon={ClipboardList} title="No applications" /> : (
        <div className="bg-card border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60"><tr className="text-left">
              {["ID", "Applicant", "Type", "Mobile", "City", "Status", "Action"].map((h) => <th key={h} className="p-3 font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.application_id} className="border-t" data-testid={`app-row-${a.application_id}`}>
                  <td className="p-3 font-mono text-xs">{a.display_id}</td>
                  <td className="p-3">{a.applicant_name}<div className="text-xs text-muted-foreground">{a.email}</div></td>
                  <td className="p-3 capitalize">{a.type}</td>
                  <td className="p-3">{a.mobile}</td>
                  <td className="p-3">{a.city}</td>
                  <td className="p-3"><span className={`text-[11px] rounded-full px-2 py-0.5 ${STATUS_TONE[a.status] || "bg-muted"}`}>{a.status.replace(/_/g, " ")}</span></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openDetail(a.application_id)} data-testid={`app-view-${a.application_id}`}>View</Button>
                      {!["APPROVED", "ACTIVATED", "REJECTED"].includes(a.status) && (
                        <Button size="sm" className="rounded-full gap-1" onClick={() => { if (window.confirm(`Approve ${a.display_id}?`)) act(a.application_id, "approve", {}, "Approved & activated"); }} data-testid={`app-quick-approve-${a.application_id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />Approve
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="app-detail-dialog">
          {detail && (() => {
            const a = detail.application; const d = a.data || {};
            return (
              <>
                <DialogHeader><DialogTitle className="font-display flex items-center gap-2">{a.display_id} · {a.applicant_name}
                  <span className={`text-[11px] rounded-full px-2 py-0.5 ${STATUS_TONE[a.status] || "bg-muted"}`}>{a.status.replace(/_/g, " ")}</span></DialogTitle>
                  <DialogDescription>Review application details, documents and history, then approve, reject, hold or request more information.</DialogDescription></DialogHeader>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>Type: <b className="capitalize">{a.type}</b></div>
                    <div>Submitted: {new Date(a.created_at).toLocaleDateString()}</div>
                    <div className="flex items-center gap-1">Mobile {a.mobile} {a.mobile_verified && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}</div>
                    <div className="flex items-center gap-1 break-all">Email {a.email} {a.email_verified && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}</div>
                    {a.category && <div>Category: {a.category}</div>}
                    {d.qualification && <div>Qualification: {d.qualification}</div>}
                    {(d.experience) && <div>Experience: {d.experience}</div>}
                    {(d.society_name || d.business_name) && <div>Name: {d.society_name || d.business_name}</div>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a href={`tel:${a.mobile}`}><Button size="sm" variant="outline" className="rounded-full gap-1"><Phone className="w-3.5 h-3.5" />Call</Button></a>
                    <a href={`https://wa.me/91${(a.mobile || "").replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full gap-1"><MessageSquare className="w-3.5 h-3.5" />WhatsApp</Button></a>
                    <a href={`mailto:${a.email}`}><Button size="sm" variant="outline" className="rounded-full gap-1"><Mail className="w-3.5 h-3.5" />Email</Button></a>
                    {detail.crm_lead && <a href={`/admin/leads?q=${encodeURIComponent(detail.crm_lead.display_id)}`} data-testid="app-view-crm-lead"><Button size="sm" variant="outline" className="rounded-full">View CRM Lead</Button></a>}
                  </div>
                  {detail.crm_lead && <div className="border rounded-lg p-3 text-xs" data-testid="app-crm-summary">
                    <b>{detail.crm_lead.display_id}</b> · Source: {detail.crm_lead.source || "—"} · Assigned sales: {detail.crm_lead.assigned_to || "Unassigned"}
                    <span className="block mt-1 text-muted-foreground">Previous follow-ups: {(detail.crm_followups || []).length}</span>
                  </div>}
                  <div className="flex flex-wrap gap-2 border-t pt-3">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(a.application_id, "status", { status: "UNDER_REVIEW" }, "Marked under review")} data-testid="app-review">Under Review</Button>
                    <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => act(a.application_id, "status", { status: "CONTACTED" }, "Marked contacted")}>Mark Contacted</Button>
                    <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => act(a.application_id, "meeting", { mode: "Phone" }, "Meeting scheduled")}><CalendarClock className="w-3.5 h-3.5" />Schedule Meeting</Button>
                    <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => act(a.application_id, "request-document", { message: "Please upload your registration & PAN documents." }, "Documents requested")}><FileWarning className="w-3.5 h-3.5" />Request Docs</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(a.application_id, "status", { status: "ON_HOLD" }, "On hold")}>Hold</Button>
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Add internal note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="app-note-input" />
                    <Button size="sm" onClick={() => { act(a.application_id, "note", { note }, "Note added"); setNote(""); }} className="rounded-full">Add</Button>
                  </div>
                  {detail.notes.length > 0 && <div className="text-xs text-muted-foreground space-y-1">{detail.notes.map((n) => <div key={n.note_id}>• {n.note} <span className="opacity-60">— {n.by}</span></div>)}</div>}
                  <div className="border-t pt-2">
                    <div className="font-semibold text-xs mb-1">Timeline</div>
                    {detail.history.map((h, i) => <div key={i} className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString()} · {h.old_status} → <b>{h.new_status}</b> {h.note && `(${h.note})`}</div>)}
                  </div>
                  <div className="border-t pt-2">
                    <div className="font-semibold text-xs mb-1">Documents ({(detail.documents || []).length})</div>
                    {(detail.documents || []).length === 0 ? (
                      <div className="text-xs text-muted-foreground">No documents uploaded yet. Use "Request Docs" to ask the applicant.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {detail.documents.map((d) => (
                          <a key={d.doc_id} href={`${API}/application-documents/${d.doc_id}/file`} target="_blank" rel="noreferrer"
                            className="flex items-center justify-between border rounded-lg p-2 text-sm hover:border-primary" data-testid={`app-doc-${d.doc_id}`}>
                            <span className="flex items-center gap-2"><FileWarning className="w-3.5 h-3.5 text-primary" /><b>{d.doc_type}</b> <span className="text-muted-foreground text-xs">· {d.filename}</span></span>
                            <Download className="w-3.5 h-3.5 text-muted-foreground" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  {a.status !== "REJECTED" && <Button variant="outline" className="rounded-full gap-1 text-destructive" onClick={() => act(a.application_id, "reject", { reason: "Not approved" }, "Rejected")} data-testid="app-reject"><XCircle className="w-4 h-4" />Reject</Button>}
                  {!["APPROVED", "ACTIVATED"].includes(a.status) && <Button className="rounded-full gap-1" onClick={() => { if (window.confirm("Approve this application?")) act(a.application_id, "approve", {}, "Approved & activated"); }} data-testid="app-approve"><CheckCircle2 className="w-4 h-4" />Approve</Button>}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
